import { ref, set, get, push, onValue, update } from 'firebase/database';
import { db } from '../lib/firebase';
import { sendNotification } from './notificationService';

export type ChallengeType = 'points' | 'crime_game' | 'daily_training' | 'word_of_day';

export interface ChallengeData {
  id?: string;
  challengeType: ChallengeType;
  title: string;
  challengerId: string;
  challengedId: string;
  challengerName: string;
  challengedName: string;
  challengerPhoto?: string;
  challengedPhoto?: string;
  startTime: number;
  endTime: number;
  challengerScore: number;
  challengedScore: number;
  challengerStartScore: number;
  challengedStartScore: number;
  status: 'active' | 'completed';
  winnerId?: string;
}

const CHALLENGE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export const createChallenge = async (challengerId: string, challengedId: string, challengeType: ChallengeType = 'points') => {
  // Get both users data
  const [challengerSnap, challengedSnap] = await Promise.all([
    get(ref(db, `users/${challengerId}`)),
    get(ref(db, `users/${challengedId}`))
  ]);
  
  const challenger = challengerSnap.val();
  const challenged = challengedSnap.val();
  
  if (!challenger || !challenged) return null;
  
  const startTime = Date.now();
  const endTime = startTime + CHALLENGE_DURATION_MS;
  
  const challengeRef = push(ref(db, 'challenges'));
  const challengeId = challengeRef.key;
  
  const titleMap: Record<ChallengeType, string> = {
    points: 'Points Race',
    crime_game: 'Crime Game',
    daily_training: 'Daily Training',
    word_of_day: 'Word of the Day'
  };
  
  const rawChallengeData: Omit<ChallengeData, 'id'> = {
    challengeType,
    title: titleMap[challengeType] || 'Challenge',
    challengerId,
    challengedId,
    challengerName: challenger.username || challenger.displayName || 'Player 1',
    challengedName: challenged.username || challenged.displayName || 'Player 2',
    challengerPhoto: challenger.profilePhotoUrl || challenger.photoURL,
    challengedPhoto: challenged.profilePhotoUrl || challenged.photoURL,
    startTime,
    endTime,
    challengerStartScore: challenger.score || challenger.totalPoints || 0,
    challengedStartScore: challenged.score || challenged.totalPoints || 0,
    challengerScore: 0,
    challengedScore: 0,
    status: 'active'
  };
  
  // Sanitize undefined values for Firebase Realtime DB
  const challengeData = Object.fromEntries(
    Object.entries(rawChallengeData).filter(([_, v]) => v !== undefined)
  ) as Omit<ChallengeData, 'id'>;
  
  await set(challengeRef, challengeData);
  
  // Notify the challenged user
  const typeNameMap: Record<ChallengeType, string> = {
    points: 'point race',
    crime_game: 'Crime Game',
    daily_training: 'Daily Training',
    word_of_day: 'Word of the Day'
  };

  await sendNotification(challengedId, {
    type: 'challenge_received',
    message: `${challengeData.challengerName} challenged you to a 24-hour ${typeNameMap[challengeType]} race!`,
    fromUserId: challengerId,
    fromUserName: challengeData.challengerName,
    fromUserPhoto: challengeData.challengerPhoto,
    metadata: { challengeId, challengeType }
  });
  
  return challengeId;
};

export const getChallengeById = async (challengeId: string): Promise<ChallengeData | null> => {
  const challengeSnap = await get(ref(db, `challenges/${challengeId}`));
  if (challengeSnap.exists()) {
    return { id: challengeId, ...challengeSnap.val() };
  }
  return null;
};

export const getActiveChallenges = (userId: string, callback: (challenges: ChallengeData[]) => void) => {
  const challengesRef = ref(db, 'challenges');
  
  return onValue(challengesRef, (snapshot) => {
    const data = snapshot.val() || {};
    const activeChallenges: ChallengeData[] = [];
    const now = Date.now();
    
    Object.entries<any>(data).forEach(([id, val]) => {
      if ((val.challengerId === userId || val.challengedId === userId) && val.status === 'active') {
        // Auto-complete if time expired
        if (now > val.endTime) {
          completeChallenge(id, val);
        } else {
          activeChallenges.push({ id, ...val });
        }
      }
    });
    
    callback(activeChallenges);
  });
};

export const getCompletedChallenges = (userId: string, callback: (challenges: ChallengeData[]) => void) => {
  const challengesRef = ref(db, 'challenges');
  
  return onValue(challengesRef, (snapshot) => {
    const data = snapshot.val() || {};
    const completedChallenges: ChallengeData[] = [];
    
    Object.entries<any>(data).forEach(([id, val]) => {
      if ((val.challengerId === userId || val.challengedId === userId) && val.status === 'completed') {
        completedChallenges.push({ id, ...val });
      }
    });
    
    // Sort descending by end time
    completedChallenges.sort((a, b) => b.endTime - a.endTime);
    
    callback(completedChallenges);
  });
};

const completeChallenge = async (challengeId: string, challenge: ChallengeData) => {
  const isDraw = challenge.challengerScore === challenge.challengedScore;
  let winnerId = null;
  if (!isDraw) {
    winnerId = challenge.challengerScore > challenge.challengedScore ? challenge.challengerId : challenge.challengedId;
  }
  
  await update(ref(db, `challenges/${challengeId}`), {
    status: 'completed',
    winnerId
  });
  
  // Notify both
  const resultMsg = winnerId 
    ? (winnerId === challenge.challengerId ? `${challenge.challengerName} won the challenge!` : `${challenge.challengedName} won the challenge!`)
    : "The challenge ended in a draw!";
    
  await Promise.all([
    sendNotification(challenge.challengerId, {
      type: 'challenge_finished',
      message: resultMsg,
      fromUserId: challenge.challengedId,
      fromUserName: challenge.challengedName,
      fromUserPhoto: challenge.challengedPhoto,
      metadata: { challengeId }
    }),
    sendNotification(challenge.challengedId, {
      type: 'challenge_finished',
      message: resultMsg,
      fromUserId: challenge.challengerId,
      fromUserName: challenge.challengerName,
      fromUserPhoto: challenge.challengerPhoto,
      metadata: { challengeId }
    })
  ]);
};

// Called when a user's score updates
export const processScoreUpdateForChallenges = async (userId: string, newTotalScore: number) => {
  const challengesSnap = await get(ref(db, 'challenges'));
  const data = challengesSnap.val() || {};
  const now = Date.now();
  
  const updates: Record<string, any> = {};
  
  for (const [id, val] of Object.entries<any>(data)) {
    if (val.status === 'active' && (val.challengerId === userId || val.challengedId === userId)) {
      // Only process types that are based on global score
      if (val.challengeType !== 'points') {
        continue;
      }
      
      if (now > val.endTime) {
        // Expired, should be handled by next read or we handle it here
        await completeChallenge(id, val);
        continue;
      }
      
      if (val.challengerId === userId) {
        updates[`${id}/challengerScore`] = newTotalScore - val.challengerStartScore;
      } else {
        updates[`${id}/challengedScore`] = newTotalScore - val.challengedStartScore;
      }
    }
  }
  
  if (Object.keys(updates).length > 0) {
    await update(ref(db, 'challenges'), updates);
  }
};

// Increment challenge score directly for specific challenge types (e.g., crime_game)
export const incrementChallengeScore = async (userId: string, addedPoints: number, challengeType: ChallengeType) => {
  if (addedPoints <= 0) return;
  
  const challengesSnap = await get(ref(db, 'challenges'));
  const data = challengesSnap.val() || {};
  const now = Date.now();
  
  const updates: Record<string, any> = {};
  
  for (const [id, val] of Object.entries<any>(data)) {
    if (val.status === 'active' && val.challengeType === challengeType && (val.challengerId === userId || val.challengedId === userId)) {
      if (now > val.endTime) {
        // Expired
        await completeChallenge(id, val);
        continue;
      }
      
      if (val.challengerId === userId) {
        updates[`${id}/challengerScore`] = (val.challengerScore || 0) + addedPoints;
      } else {
        updates[`${id}/challengedScore`] = (val.challengedScore || 0) + addedPoints;
      }
    }
  }
  
  if (Object.keys(updates).length > 0) {
    await update(ref(db, 'challenges'), updates);
  }
};
